import { SignInProviderConfig, UserIdentity } from '@backstage/core-components';
import {
  BackstageIdentityResponse,
  SignInPageProps,
  useApiHolder,
} from '@backstage/core-plugin-api';
import { useMountEffect } from '@react-hookz/web';
import { ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type BackstageSignInProps = Omit<SignInProviderConfig, 'message'> & {
  subheading?: string;
  message: string | ReactNode;
};

export type BackstageSignInPageProps = SignInPageProps & {
  providers: BackstageSignInProps[];
  auto?: boolean;
};

export type SignInPageOutput = {
  showLoginPage: boolean;
  login: (opts: {
    checkExisting?: boolean;
    showPopup?: boolean;
  }) => Promise<void>;
  error?: Error;
};

export const useSignInPage = ({
  providers,
  auto,
  onSignInSuccess,
}: BackstageSignInPageProps) => {
  const [error, setError] = useState<Error>();
  // The SignIn component takes some time to decide whether the user is logged-in or not.
  // showLoginPage is used to prevent a glitch-like experience where the sign-in page is
  // displayed for a split second when the user is already logged-in.
  const [showLoginPage, setShowLoginPage] = useState<boolean>(false);
  const [userSelectedProvider, setUserSelectedProvider] = useState<
    string | undefined
  >(undefined);

  // User was redirected back to sign in page with error from auth redirect flow
  const [searchParams, setSearchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  const apiHolder = useApiHolder();
  // This is set based on the user selected provider, if none selected then it defaults to first one.
  // const authApi = useApi((() => {
  //   const inx = providers.findIndex(p => p.id === userSelectedProvider);
  //   return inx !== -1 ? providers[inx].apiRef : providers[0].apiRef;
  // })());

  type LoginOpts = {
    checkExisting?: boolean;
    showPopup?: boolean;
  };
  const login = async ({ checkExisting, showPopup }: LoginOpts) => {
    try {
      let providerIndex = 0;
      if (userSelectedProvider) {
        const index = providers.findIndex(p => p.id === userSelectedProvider);
        if (index !== -1) {
          providerIndex = index;
        }
      }

      let selectedProvider = providers[providerIndex];
      let authApi = apiHolder.get(selectedProvider.apiRef);
      let identityResponse: BackstageIdentityResponse | undefined;

      if (checkExisting) {
        // this will be true only on initial mount
        let localApiAuth;
        // Do an initial check if any logged-in session exists
        for (let i = 0; i < providers.length; i++) {
          localApiAuth = apiHolder.get(providers[i].apiRef);
          identityResponse = await localApiAuth?.getBackstageIdentity({
            optional: true,
          });
          if (identityResponse) {
            // found an existing session
            selectedProvider = providers[i];
            authApi = localApiAuth;
            providerIndex = i;
            setUserSelectedProvider(selectedProvider.id);
            break;
          }
        }
      }

      // If no session exists, or called from login page then show the sign-in page
      if (
        selectedProvider &&
        !identityResponse &&
        authApi &&
        (showPopup || auto) &&
        !errorParam
      ) {
        // Unless auto is set to true, this step should not happen.
        // When user intentionally clicks the Sign In button, autoShowPopup is set to true
        setShowLoginPage(true);
        identityResponse = await authApi.getBackstageIdentity({
          instantPopup: true,
        });
        if (!identityResponse) {
          throw new Error(
            `The ${selectedProvider.title} provider is not configured to support sign-in`,
          );
        }
      }

      // Since the call was made from the login page without provider selection or
      // was not from onMountEffect the identityResponse was undefined. Throw back to show login page.
      if (!identityResponse) {
        setShowLoginPage(true);
        return;
      }

      // Identity has been obtained successfully and now set the user profile from onSignInSuccess
      if (selectedProvider && authApi) {
        const profile = await authApi.getProfile();

        onSignInSuccess(
          UserIdentity.create({
            identity: identityResponse.identity,
            authApi,
            profile,
          }),
        );
      } else {
        setShowLoginPage(true);
      }
    } catch (err: any) {
      // console.error('Sign-in error:', err, 'Type:', err?.name, 'Message:', err?.message);
      // User closed the sign-in modal
      setError(err);
      setShowLoginPage(true);
    }
  };

  useMountEffect(() => {
    if (errorParam) {
      setError(new Error(errorParam));
      // Clear the error from the URL
      setSearchParams({});
    }
    login({ checkExisting: true });
  });

  return {
    showLoginPage,
    login,
    error,
    userSelectedProvider,
    setUserSelectedProvider,
  };
};
